/**
 * UUID Version 7 Decoder
 *
 * UUID v7 is a time-ordered UUID format defined in RFC 9562.
 * Format: xxxxxxxx-xxxx-7xxx-yxxx-xxxxxxxxxxxx
 *
 * Structure:
 * - 48 bits: Unix timestamp in milliseconds
 * - 4 bits: Version (0111 = 7)
 * - 12 bits: Sub-millisecond precision or random data
 * - 2 bits: Variant (10)
 * - 62 bits: Random data
 *
 * @see https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-7
 */

export interface UUIDv7DecodeResult {
  isValid: boolean;
  uuid?: string;
  version?: number;
  variant?: string;
  timestamp?: number;
  timestampHex?: string;
  date?: string;
  subsecPrecision?: string;
  subsecPrecisionHex?: string;
  randomness?: string;
  error?: string;
}

/**
 * Validates if the input string is a valid UUID v7
 */
export function isValidUUIDv7(input: string): boolean {
  if (!input) return false;

  // Remove any whitespace
  const trimmed = input.trim();

  // UUID format: 8-4-4-4-12 (36 characters total with hyphens)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!uuidRegex.test(trimmed)) {
    return false;
  }

  // Check version (13th character should be '7')
  const versionChar = trimmed.charAt(14); // Position 14 (0-indexed) is after "xxxxxxxx-xxxx-"
  if (versionChar !== '7') {
    return false;
  }

  // Check variant (17th hex digit should be 8, 9, a, or b)
  const variantChar = trimmed.charAt(19).toLowerCase(); // Position 19 is after "xxxxxxxx-xxxx-7xxx-"
  if (!['8', '9', 'a', 'b'].includes(variantChar)) {
    return false;
  }

  // Validate timestamp is reasonable (not in far future, not before UUID v7 spec)
  try {
    const timestampHex = trimmed.substring(0, 8) + trimmed.substring(9, 13);
    const timestamp = parseInt(timestampHex, 16);

    // UUID v7 spec was published in 2024, but implementations existed earlier
    // Let's use 2020 as a reasonable lower bound
    const minTimestamp = new Date('2020-01-01T00:00:00Z').getTime();
    const now = Date.now();
    const maxTimestamp = now + (365 * 24 * 60 * 60 * 1000 * 10); // 10 years in future

    if (timestamp < minTimestamp || timestamp > maxTimestamp) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Decodes a UUID v7 and extracts its components
 */
export function decodeUUIDv7(input: string): UUIDv7DecodeResult {
  if (!input) {
    return {
      isValid: false,
      error: 'Input is empty'
    };
  }

  const trimmed = input.trim();

  if (!isValidUUIDv7(trimmed)) {
    return {
      isValid: false,
      error: 'Invalid UUID v7 format'
    };
  }

  try {
    // Normalize to lowercase
    const uuid = trimmed.toLowerCase();

    // Extract components (remove hyphens for easier parsing)
    const hex = uuid.replace(/-/g, '');

    // Timestamp: first 48 bits (12 hex characters)
    const timestampHex = hex.substring(0, 12);
    const timestamp = parseInt(timestampHex, 16);
    const date = new Date(timestamp).toISOString();

    // Version: next 4 bits (1 hex character)
    const versionHex = hex.substring(12, 13);
    const version = parseInt(versionHex, 16);

    // Sub-second precision / random: next 12 bits (3 hex characters)
    const subsecPrecisionHex = hex.substring(13, 16);
    const subsecPrecision = parseInt(subsecPrecisionHex, 16).toString();

    // Variant: next 2 bits (part of 1 hex character)
    const variantHex = hex.substring(16, 17);
    const variantValue = parseInt(variantHex, 16);
    let variant = 'Unknown';
    if ((variantValue & 0b1100) === 0b1000) {
      variant = 'RFC 9562 (10xx)';
    }

    // Randomness: remaining 62 bits (15.5 hex characters)
    const randomnessHex = hex.substring(16, 32);
    const randomness = randomnessHex;

    return {
      isValid: true,
      uuid,
      version,
      variant,
      timestamp,
      timestampHex,
      date,
      subsecPrecision,
      subsecPrecisionHex,
      randomness
    };
  } catch (error) {
    return {
      isValid: false,
      error: `Decoding error: ${error instanceof Error ? error.message : 'Unknown error'}`
    };
  }
}

/**
 * Formats the decoded UUID v7 result for display
 */
export function formatUUIDv7Result(result: UUIDv7DecodeResult): string {
  if (!result.isValid) {
    return `❌ ${result.error || 'Invalid UUID v7'}`;
  }

  const lines = [
    '✅ Valid UUID Version 7',
    '',
    `UUID: ${result.uuid}`,
    `Version: ${result.version}`,
    `Variant: ${result.variant}`,
    '',
    '📅 Timestamp Information:',
    `  Timestamp (ms): ${result.timestamp}`,
    `  Timestamp (hex): 0x${result.timestampHex}`,
    `  Date (ISO): ${result.date}`,
    '',
    '🎲 Random Components:',
    `  Sub-sec precision: ${result.subsecPrecision} (0x${result.subsecPrecisionHex})`,
    `  Randomness (hex): ${result.randomness}`,
    '',
    '📖 Learn more: https://www.rfc-editor.org/rfc/rfc9562.html#name-uuid-version-7'
  ];

  return lines.join('\n');
}
